/**
 * HTTP layer for bulk firmware-upgrade batches. Start, preflight, poll, and
 * cancel a fleet firmware rollout.
 *
 * Contains:
 * - getFirmwareUpgradeBatch: poll a batch's status
 * - startFirmwareUpgradeBulk / preflightFirmwareUpgradeBulk: launch and dry-run a rollout
 * - cancelFirmwareUpgradeBatch: cancel an in-flight batch
 */
import apiClient from '@sophocles/http-common';
import { buildUpgradeTypeField } from '@sophocles/views/admin/engineering/firmwareUpgrades/firmwareUpgradeHelpers';

// Firmware-upgrade endpoints are served under odl_app.
const BASE = '/odl-app/firmware-upgrades';

export const getFirmwareUpgradeBatch = batchId => apiClient.get(`${BASE}/${batchId}/`);

export const startFirmwareUpgradeBulk = ({ firmwareVersionId, iccids, upgradeType, notes }) =>
  apiClient.post(`${BASE}/`, {
    firmware_version_id: firmwareVersionId,
    iccids,
    ...buildUpgradeTypeField(upgradeType),
    notes,
  });

// Dry-run validation: returns per-ICCID match/mismatch/unknown/unresolved
// classification against the target firmware's major version. Creates
// no batch and never enqueues — safe to call as the user types.
// ``upgrade_type`` matters for the no-IP check: PERSISTENT skips it,
// sync paths require radio.ip_address. When omitted (Auto) the backend
// derives the path per unit, so persistent-model units aren't flagged.
export const preflightFirmwareUpgradeBulk = ({ firmwareVersionId, iccids, upgradeType }) =>
  apiClient.post(`${BASE}/preflight/`, {
    firmware_version_id: firmwareVersionId,
    iccids,
    ...buildUpgradeTypeField(upgradeType),
  });

// Cooperative cancel — backend marks the batch CANCELLING, force-fails
// in-flight log rows, reconciles to CANCELLED, and (best-effort)
// revokes the celery task. Daemon-path batches close out in pliny but
// the unit may still receive the firmware; caller should warn.
export const cancelFirmwareUpgradeBatch = batchId => apiClient.post(`${BASE}/${batchId}/cancel/`);
